/**
 * First-party pseudonymous visitor id (CONTRACTS §J.2): cookie `aca_vid` (SameSite=Lax, 365 days) + localStorage,
 * created **only** with analytics consent. Without consent nothing is read from or written to the device.
 */
export const VISITOR_COOKIE = 'aca_vid';
const MAX_AGE = 365 * 24 * 3600;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  try {
    crypto.getRandomValues(b);
  } catch {
    for (let i = 0; i < n; i++) b[i] = Math.floor(Math.random() * 256);
  }
  return b;
}

/** UUID v7: 48-bit Unix ms timestamp, version 7, RFC 4122 variant, 74 random bits. */
export function uuidv7(now = Date.now()): string {
  const b = randomBytes(16);
  let ts = now;
  for (let i = 5; i >= 0; i--) {
    b[i] = ts % 256;
    ts = Math.floor(ts / 256);
  }
  b[6] = (b[6]! & 0x0f) | 0x70;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function readCookie(): string | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)aca_vid=([^;]*)/);
    return m ? decodeURIComponent(m[1]!) : null;
  } catch {
    return null;
  }
}

function writeCookie(value: string, maxAge: number): void {
  try {
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${VISITOR_COOKIE}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies blocked */
  }
}

function readStorage(): string | null {
  try {
    return window.localStorage.getItem(VISITOR_COOKIE);
  } catch {
    return null;
  }
}

function writeStorage(value: string | null): void {
  try {
    if (value) window.localStorage.setItem(VISITOR_COOKIE, value);
    else window.localStorage.removeItem(VISITOR_COOKIE);
  } catch {
    /* storage blocked */
  }
}

/** Existing or new visitor id. Call only with `consent.analytics === true`. Refreshes the cookie expiry. */
export function getOrCreateVisitorId(): string {
  const existing = [readCookie(), readStorage()].find((v): v is string => !!v && UUID_RE.test(v));
  const id = (existing ?? uuidv7()).toLowerCase();
  writeCookie(id, MAX_AGE);
  writeStorage(id);
  return id;
}

/** Consent denied/revoked: remove any stored id. */
export function clearVisitorId(): void {
  if (readCookie() !== null) writeCookie('', 0);
  writeStorage(null);
}
